<template>
    <aside class="sidebar overflow-y-auto flex flex-col" :class="{ collapsed }">
        <div class="nav_box w-full">
            <template v-for="item in visibleNavItems" :key="item.key">
                <div v-if="item.type === 'divider'" class="sidebar_divider"></div>
                <template v-else>
                    <div :class="['nav_item d-flex-y pointer relative', { 'active': active === item.section, 'open': item.group !== undefined && openGroups[item.group] }]"
                        :title="item.label" @click="handleNavItem(item)">
                        <img class="ico" :src="item.icon" alt="">
                        <span class="item_text">{{ item.label }}</span>
                        <span v-if="item.children" class="nav_arrow">›</span>
                    </div>
                    <div v-if="item.children && item.group"
                        :class="['nav_sub overflow-hidden', { 'open': openGroups[item.group] }]">
                        <div v-for="child in item.children" :key="child.key"
                            :class="['nav_sub_item d-flex-y pointer relative', { 'active': active === child.section }]"
                            @click="goSubPage(item.group, child.pageId)">
                            <img class="ico" :src="child.icon" alt="">
                            <span>{{ child.label }}</span>
                        </div>
                    </div>
                </template>
            </template>
        </div>

        <!-- ── 底部折叠/展开按钮 ── -->
        <div class="sb_toggle_row w-full d-flex-between">
            <span class="sb_brand_mini"></span>
            <button class="sb_toggle_btn d-flex-y pointer" :title="collapsed ? '展开导航' : '收起导航'"
                @click="collapsed = !collapsed">
                <svg class="sb_toggle_ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                </svg>
                <span class="sb_toggle_label">收起</span>
            </button>
        </div>
    </aside>
</template>

<script setup lang="ts">
type SidebarGroup = 'office' | 'office-tools' | 'mine' | 'support';

type SidebarNavChild = {
    key: string;
    section: string;
    icon: string;
    label: string;
    pageId: string;
};

type SidebarNavItem =
    | { type: 'divider'; key: string }
    | {
        type: 'item';
        key: string;
        section: string;
        icon: string;
        label: string;
        pageId?: string;
        group?: SidebarGroup;
        isNav: boolean;
        children?: SidebarNavChild[];
    };

const appStore = useAppStore();
const collapsed = ref(false);
const openGroups = reactive({
    office: false,
    'office-tools': false,
    mine: false,
    support: false,
});

const sidebarNavItems: SidebarNavItem[] = [
    {
        type: 'item',
        key: 'home',
        section: 'home',
        label: '首页',
        pageId: 'page-home',
        icon: new URL('@/assets/sidebar/sidebar_home.svg', import.meta.url).href,
        isNav: true
    },
    {
        type: 'item',
        key: 'office',
        section: 'office',
        label: 'Office',
        pageId: 'page-office',
        group: 'office',
        icon: new URL('@/assets/sidebar/sidebar_office.svg', import.meta.url).href,
        isNav: true,
        children: [
            {
                key: 'activation',
                section: 'activation',
                label: '激活码购买',
                pageId: 'page-activation',
                icon: new URL('@/assets/sidebar/sidebar_office_activation.svg', import.meta.url).href
            },
            {
                key: 'install',
                section: 'install',
                label: '安装 Office',
                pageId: 'page-install',
                icon: new URL('@/assets/sidebar/sidebar_office_install.svg', import.meta.url).href
            },
            {
                key: 'tips',
                section: 'tips',
                label: '快捷技巧',
                pageId: 'page-tips',
                icon: new URL('@/assets/sidebar/sidebar_office_tips.svg', import.meta.url).href
            },
        ],
    },
    {
        type: 'item',
        key: 'ai-lobster',
        section: 'ai-lobster',
        label: 'AI & 智能体',
        pageId: 'page-ai-lobster',
        icon: new URL('@/assets/sidebar/sidebar_aiLobster.svg', import.meta.url).href,
        isNav: false
    },
    {
        type: 'item',
        key: 'office-tools',
        section: 'office-tools',
        label: '办公软件',
        group: 'office-tools',
        icon: new URL('@/assets/sidebar/sidebar_tools.svg', import.meta.url).href,
        isNav: true,
        children: [
            {
                key: 'compress',
                section: 'compress',
                label: '压缩',
                pageId: 'page-compress',
                icon: new URL('@/assets/sidebar/sidebar_tools_compress.svg', import.meta.url).href,
            },
            {
                key: 'disk-clean',
                section: 'disk-clean',
                label: 'C盘清理',
                pageId: 'page-disk-clean',
                icon: new URL('@/assets/sidebar/sidebar_tools_clear.svg', import.meta.url).href,
            },
            {
                key: 'image-viewer',
                section: 'image-viewer',
                label: '看图',
                pageId: 'page-image-viewer',
                icon: new URL('@/assets/sidebar/sidebar_tools_viewer.svg', import.meta.url).href,
            },
            {
                key: 'pdf-tools',
                section: 'pdf-tools',
                label: 'PDF',
                pageId: 'page-pdf-tools',
                icon: new URL('@/assets/sidebar/sidebar_tools_pdf.svg', import.meta.url).href,
            },
        ],
    },
    {
        type: 'item',
        key: 'plugin-plaza',
        section: 'plugin-plaza',
        label: 'Ai插件广场',
        pageId: 'page-plugin-plaza',
        icon: new URL('@/assets/sidebar/sidebar_plugin.svg', import.meta.url).href,
        isNav: true
    },
    { type: 'divider', key: 'account-divider' },
    {
        type: 'item',
        key: 'mine',
        section: 'mine',
        label: '我的',
        group: 'mine',
        icon: new URL('@/assets/sidebar/sidebar_mine.svg', import.meta.url).href,
        isNav: true,
        children: [
            {
                key: 'orders',
                section: 'orders',
                label: '我的订单',
                pageId: 'page-orders',
                icon: new URL('@/assets/sidebar/sidebar_mine_order.svg', import.meta.url).href,
            },
            {
                key: 'plugins-mine',
                section: 'plugins-mine',
                label: '已安装插件',
                pageId: 'page-plugins-mine',
                icon: new URL('@/assets/sidebar/sidebar_mine_plugin.svg', import.meta.url).href,
            },
        ],
    },
    { type: 'divider', key: 'support-divider' },
    {
        type: 'item',
        key: 'support',
        section: 'support',
        label: '支持',
        group: 'support',
        icon: new URL('@/assets/sidebar/sidebar_support.svg', import.meta.url).href,
        isNav: true,
        children: [
            {
                key: 'help',
                section: 'help',
                label: '帮助中心',
                pageId: 'page-help',
                icon: new URL('@/assets/sidebar/sidebar_support_help.svg', import.meta.url).href,
            },
            {
                key: 'contact',
                section: 'contact',
                label: '联系客服',
                pageId: 'page-contact',
                icon: new URL('@/assets/sidebar/sidebar_support_contact.svg', import.meta.url).href,
            },
        ],
    }
];

// 未展示全部功能（C++ client_get_show_all_state 返回 show=false）时隐藏的入口。
// plugins-mine 是「我的」分组下的子项：插件广场已隐藏，已安装插件页也就没有意义。
const RESTRICTED_KEYS = ['office-tools', 'plugin-plaza', 'plugins-mine'];

const visibleNavItems = computed(() => {
    const items = sidebarNavItems.filter(it => it.type === 'divider' || it.isNav);
    if (appStore.showAllFeatures) return items;

    return items.reduce<SidebarNavItem[]>((acc, it) => {
        if (it.type === 'divider') {
            acc.push(it);
            return acc;
        }
        if (RESTRICTED_KEYS.includes(it.key)) return acc;

        // 子项也要过滤，且不能改动原始配置对象（浅拷贝后替换 children）
        const children = it.children?.filter(child => !RESTRICTED_KEYS.includes(child.key));
        if (children?.length === it.children?.length) {
            acc.push(it);
        } else {
            // 过滤后为空时置 undefined，否则模板仍会渲染展开箭头和空的子菜单容器
            acc.push({ ...it, children: children?.length ? children : undefined });
        }
        return acc;
    }, []);
});

const active = computed(() => appStore.activeSidebarSection);

function closeGroupsExcept(group?: keyof typeof openGroups): void {
    (Object.keys(openGroups) as Array<keyof typeof openGroups>).forEach((key) => {
        openGroups[key] = key === group ? openGroups[key] : false;
    });
}

function goTopPage(pageId: string): void {
    closeGroupsExcept();
    appStore.goPage(pageId);
}

function handleNavItem(item: Exclude<SidebarNavItem, { type: 'divider' }>): void {
    if (item.group) {
        const nextOpen = !openGroups[item.group];
        closeGroupsExcept(item.group);
        openGroups[item.group] = nextOpen;
        if (item.pageId) {
            appStore.goPage(item.pageId);
            return;
        }
        appStore.setSidebarActive(item.section);
        return;
    }

    if (item.pageId) {
        goTopPage(item.pageId);
    }
}

function goSubPage(group: keyof typeof openGroups, pageId: string): void {
    closeGroupsExcept(group);
    openGroups[group] = true;
    appStore.goPage(pageId);
}

watch(
    () => appStore.activeSidebarSection,
    (section) => {
        if (['office', 'activation', 'install', 'tips'].includes(section)) {
            closeGroupsExcept('office');
            openGroups.office = true;
            return;
        }
        if (['office-tools', 'disk-clean', 'image-viewer', 'compress', 'pdf-tools'].includes(section)) {
            closeGroupsExcept('office-tools');
            openGroups['office-tools'] = true;
            return;
        }
        if (['mine', 'orders', 'plugins-mine'].includes(section)) {
            closeGroupsExcept('mine');
            openGroups.mine = true;
            return;
        }
        if (['support', 'help', 'contact'].includes(section)) {
            closeGroupsExcept('support');
            openGroups.support = true;
            return;
        }
        closeGroupsExcept();
    },
    { immediate: true }
);
</script>

<style lang="less" scoped>
.sidebar {
    width: var(--sidebar-w);
    background: var(--sb-bg);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-right: 1px solid rgba(255, 255, 255, .55);
    padding: 8px 0 0;
    flex-shrink: 0;
    box-shadow: 2px 0 20px rgba(108, 78, 246, .07);
    transition: width .26s cubic-bezier(.4, 0, .2, 1);

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(108, 78, 246, .2);
        border-radius: 4px;
    }
}

.sidebar.collapsed {
    width: var(--sidebar-w-collapsed);
}

.nav_box {
    padding: 6px 0;
    margin-top: 4px;
    flex: 1;
}

.nav_item {
    gap: 10px;
    padding: 9px 12px;
    margin: 1px 8px;
    font-size: 13px;
    color: var(--sb-txt);
    border-radius: 8px;
    transition: background .15s, color .15s;
    user-select: none;

    &:hover {
        background: var(--sb-hover);
        color: #0F172A;
    }

    &.active {
        background: var(--sb-active-bg);
        color: var(--sb-active-txt);
        font-weight: 600;

        &::before {
            content: '';
            position: absolute;
            left: -8px;
            top: 6px;
            bottom: 6px;
            width: 3px;
            background: var(--sb-accent);
            border-radius: 0 3px 3px 0;
        }
    }

    .ico {
        width: 16px;
        flex-shrink: 0;
    }

    .item_text {
        font-size: 13px;
        white-space: nowrap;
        transition: opacity .18s;
    }
}

.nav_arrow {
    margin-left: auto;
    font-size: 12px;
    color: var(--text-muted);
    transition: transform .22s;
    flex-shrink: 0;
}

.nav_item.open .nav_arrow {
    transform: rotate(90deg);
}

.nav_sub {
    max-height: 0;
    transition: max-height .28s cubic-bezier(.4, 0, .2, 1);

    &.open {
        max-height: 300px;
    }
}

.nav_sub_item {
    gap: 8px;
    padding: 8px 12px 8px 22px;
    margin: 1px 8px;
    color: var(--sb-txt);
    border-radius: 8px;
    transition: background .12s, color .12s;

    &:hover {
        background: var(--sb-hover);
        color: #0F172A;
    }

    &.active {
        background: var(--sb-active-bg);

        span {
            color: var(--sb-active-txt);
            font-weight: 600;
        }
    }

    .ico {
        width: 16px;
        flex-shrink: 0;
    }

    span {
        font-size: 12px;
        color: var(--sb-txt);
    }
}

.sidebar_divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(108, 78, 246, .12), transparent);
    margin: 6px 14px;
}

/* ── 底部折叠行 ── */
.sb_toggle_row {
    position: sticky;
    bottom: 0;
    flex-shrink: 0;
    padding: 8px 10px 10px;
    background: linear-gradient(to bottom, rgba(245, 243, 255, 0) 0%, rgba(245, 243, 255, .98) 30%);
    border-top: 1px solid rgba(108, 78, 246, .07);
    margin-top: auto;
}

.sb_toggle_btn {
    gap: 6px;
    padding: 7px 12px;
    border-radius: 10px;
    border: 1px solid rgba(108, 78, 246, .15);
    background: #fff;
    color: #6C4EF6;
    box-shadow: 0 2px 8px rgba(108, 78, 246, .1);
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    transition: background .15s, box-shadow .15s;

    &:hover {
        background: #F5F0FF;
        box-shadow: 0 3px 12px rgba(108, 78, 246, .18);
    }
}

.sb_toggle_ico {
    transition: transform .26s;
    display: inline-flex;
    align-items: center;
}

.sb_toggle_label {
    font-size: 12px;
}

/* ── 折叠态 ── */
.sidebar.collapsed .item_text {
    opacity: 0;
    width: 0;
    overflow: hidden;
}

.sidebar.collapsed .nav_arrow {
    display: none;
}

.sidebar.collapsed .nav_sub {
    display: none !important;
    max-height: 0 !important;
}

.sidebar.collapsed .sidebar_divider {
    margin: 5px auto;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(108, 78, 246, .18);
}

.sidebar.collapsed .nav_item {
    justify-content: center;
    gap: 0; // 折叠态移除图标与（宽度为 0 的）文字间的 10px 间隙，否则图标会被顶得偏左
    padding: 7px 0;
    margin: 1px 2px;
}

.sidebar.collapsed .nav_item.active::before {
    left: 2px;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 14px;
    border-radius: 2px;
    background: linear-gradient(180deg, #6C4EF6, #A855F7);
}

.sidebar.collapsed .sb_toggle_row {
    justify-content: center;
    padding: 6px 0 8px;
    border-top: none;
    background: transparent;
}

.sidebar.collapsed .sb_toggle_btn {
    padding: 5px;
    border-radius: 8px;
    width: 26px;
    height: 26px;
    justify-content: center;
    box-shadow: none;
}

.sidebar.collapsed .sb_toggle_label {
    display: none;
}

.sidebar.collapsed .sb_toggle_ico {
    transform: rotate(180deg);
}
</style>
